import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/firebase";
import { doc, getDoc } from "firebase/firestore";
import { Link } from "react-router-dom";
import CustomAlert from "../components/CustomAlert";
import PaystackCheckoutOneTime from "../components/PaystackCheckout";

export default function BillingSettings() {
  const { user, loading: authLoading } = useAuth();

  const [isPro, setIsPro] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  const [alerts, setAlerts] = useState<
    { id: number; message: string; type: "success" | "error" | "warning" }[]
  >([]);
  const [alertId, setAlertId] = useState(0);

  useEffect(() => {
    if (authLoading || !user) {
      return; // We'll handle rendering below
    }

    const userId = user.uid;

    const fetchStatus = async () => {
      try {
        // const planRef = doc(db, `users/${userId}/plan/data`);
        const userRef = doc(db, `users/${userId}`);
        const docSnap = await getDoc(userRef);
        const data = docSnap.data();
        // setIsPro(data?.isPro === true);
        setIsPro(data?.subscriptionStatus === "pro");
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch plan status:", error);
        setIsPro(false); // Default to false on error
        setLoading(false);
      }
    };

    fetchStatus();
  }, [authLoading, user]);

  const showAlert = (
    message: string,
    type: "success" | "error" | "warning" = "error"
  ) => {
    const newAlert = { id: alertId, message, type };
    setAlerts((prev) => [...prev, newAlert]);
    setAlertId(alertId + 1);
  };

  const closeAlert = (id: number) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  };

  const refreshStatus = async () => {
    if (!user) return;
    try {
      await user.reload(); // Firebase auth reload
      window.location.reload(); // Force fresh plan context
    } catch (error) {
      console.error("Failed to refresh status:", error);
      showAlert("Failed to refresh plan status. Please try again.");
    }
  };

  if (authLoading || loading) {
    return <div className="p-6 text-offwhite">Loading...</div>;
  }

  if (!user) {
    return null; // Redirect will happen via AuthContext
  }

  return (
    <div className="max-w-xl mx-auto px-6 py-10 text-sm text-offwhite">
      {alerts.map((alert) => (
        <CustomAlert
          key={alert.id}
          message={alert.message}
          type={alert.type}
          onClose={() => closeAlert(alert.id)}
        />
      ))}
      <Link to="/runtime" className="block mb-4 text-teal hover:underline">
        ← Back to Runtime
      </Link>
      <h1 className="text-2xl font-semibold mb-6 text-offwhite">
        Billing & Plans
      </h1>

      <div className="mb-4 p-4 border border-darkgray rounded-lg bg-secondary shadow-sm">
        <p className="text-gray-400">
          Your current plan:
          <span className="ml-2 font-medium text-offwhite">
            {isPro ? "Pro — Lifetime Access" : "Free"}
          </span>
        </p>
        {isPro && (
          <p className="mt-2 text-gray-400">
            ✅ You have lifetime access to all features. Thank you!
          </p>
        )}
      </div>

      {!isPro && (
        <div className="mt-4 block bg-teal hover:bg-teal/80 text-black text-center py-2 rounded-lg transition">
          <PaystackCheckoutOneTime />
        </div>
      )}

      <button
        onClick={refreshStatus}
        className="mt-4 text-gray-400 hover:text-teal text-xs underline"
      >
        Refresh Plan Status
      </button>
    </div>
  );
}
